import { useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import SubmittedVideoModal from '../components/SubmittedVideoModal'

/**
 * Shared "watch a submitted video" behavior — previously copy-pasted between
 * Home.tsx and HiredCreators.tsx (and needed a third time by MyVideos.tsx).
 * Opens the in-app SubmittedVideoModal when the video has been mirrored to R2
 * (video_storage_url set), otherwise links straight out to TikTok.
 */

/** Minimal shape the hook needs — matches cr_video_submissions columns. */
export interface WatchableVideo {
  video_storage_url: string | null
  thumbnail_url: string | null
  tiktok_video_url: string | null
}

/**
 * Some video_storage_url rows hold the literal string "null" rather than a
 * true SQL NULL (a legacy backfill quirk — BriefHub.tsx and Discover.tsx
 * guard trending_videos.video_storage_url against the exact same value).
 * Passing that string straight to <video src> throws "NotSupportedError:
 * the element has no supported sources" since "null" isn't a valid URL.
 */
export function hasStorageVideo(url: string | null | undefined): url is string {
  return !!url && url !== 'null'
}

export function useVideoWatchModal(video: WatchableVideo) {
  const [watching, setWatching] = useState(false)

  const open = useCallback(() => {
    if (hasStorageVideo(video.video_storage_url)) setWatching(true)
    else if (video.tiktok_video_url) window.open(video.tiktok_video_url, '_blank', 'noopener,noreferrer')
  }, [video.video_storage_url, video.tiktok_video_url])

  const modal = watching && hasStorageVideo(video.video_storage_url) ? createPortal(
    <SubmittedVideoModal
      videoStorageUrl={video.video_storage_url}
      thumbnailUrl={video.thumbnail_url}
      tiktokVideoUrl={video.tiktok_video_url ?? ''}
      onClose={() => setWatching(false)}
    />,
    document.body,
  ) : null

  return { open, modal }
}
